// Lightweight logger utility for frontend
// - Levels: error, warn, info, debug
// - Adds ISO timestamp prefix in development
// - Respects REACT_APP_LOG_LEVEL (error<warn<info<debug). Default: info in prod, debug in dev.
// - Patches window.console methods to route through this logger (can disable via REACT_APP_LOG_PATCH_CONSOLE=false)

const levels = { error: 0, warn: 1, info: 2, debug: 3 };

function getEnvLevel() {
  const envLevel = (process.env.REACT_APP_LOG_LEVEL || '').toLowerCase();
  if (envLevel && Object.prototype.hasOwnProperty.call(levels, envLevel)) return envLevel;
  return process.env.NODE_ENV === 'production' ? 'info' : 'debug';
}

let currentLevel = getEnvLevel();

function shouldLog(level) {
  return levels[level] <= levels[currentLevel];
}

function format(level, args) {
  const timestamp = new Date().toISOString();
  const msg = args.map(formatArg).join(' ');
  if (process.env.NODE_ENV === 'production') {
    return msg; // avoid extra noise in prod
  }
  return `[${timestamp}] ${level.toUpperCase()}: ${msg}`;
}

function formatArg(a) {
  if (a instanceof Error) return a.stack || a.message || String(a);
  if (typeof a === 'object') {
    try { return JSON.stringify(a); } catch { return '[Object]'; }
  }
  return String(a);
}

// Keep original console to avoid recursion
const originalConsole = { ...console };

const logger = {
  setLevel(level) {
    if (Object.prototype.hasOwnProperty.call(levels, level)) currentLevel = level;
  },
  getLevel() { return currentLevel; },
  error: (...args) => { if (shouldLog('error')) (originalConsole.error || originalConsole.log).call(console, format('error', args)); },
  warn:  (...args) => { if (shouldLog('warn'))  (originalConsole.warn  || originalConsole.log).call(console, format('warn', args)); },
  info:  (...args) => { if (shouldLog('info'))  (originalConsole.info  || originalConsole.log).call(console, format('info', args)); },
  debug: (...args) => { if (shouldLog('debug')) (originalConsole.debug || originalConsole.log).call(console, format('debug', args)); },
  _originalConsole: originalConsole,
};

// Patch window.console to use logger unless disabled
const patchConsole = String(process.env.REACT_APP_LOG_PATCH_CONSOLE || 'true').toLowerCase() !== 'false';

function normalizeMediapipeLog(args, fallbackLevel) {
  try {
    const txt = args.map((a) => (typeof a === 'string' ? a : (a && a.message) || '')).join(' ');
    if (!txt) return { level: fallbackLevel, args };

    // Map MediaPipe/Emscripten style prefixes to appropriate levels
    // I0000 ... -> info, W0000 ... -> warn, E0000 ... -> error
    let mappedLevel = fallbackLevel;
    if (/\bI0000\b/.test(txt)) mappedLevel = 'info';
    else if (/\bW0000\b/.test(txt)) mappedLevel = 'warn';
    else if (/\bE0000\b/.test(txt)) mappedLevel = 'error';

    // Downgrade very noisy WebGL initialization messages to debug
    if (/gl_context(?:_webgl)?\.cc:/.test(txt)) {
      if (/Successfully created a WebGL context/i.test(txt)) return { level: 'debug', args };
      if (/GL version:/i.test(txt)) return { level: 'debug', args };
      if (/OpenGL error checking is disabled/i.test(txt)) return { level: 'debug', args };
    }

    // Downgrade generic MediaPipe WASM info spam to debug
    if (/selfie_segmentation.*wasm|mediapipe/i.test(txt) && mappedLevel === 'info') {
      return { level: 'debug', args };
    }

    return { level: mappedLevel, args };
  } catch {
    return { level: fallbackLevel, args };
  }
}

if (patchConsole) {
  console.log = (...args) => logger.info(...args);
  console.info = (...args) => logger.info(...args);
  console.warn = (...args) => {
    const { level, args: a } = normalizeMediapipeLog(args, 'warn');
    return logger[level](...a);
  };
  console.error = (...args) => {
    const { level, args: a } = normalizeMediapipeLog(args, 'error');
    return logger[level](...a);
  };
  console.debug = (...args) => logger.debug(...args);
}

export default logger;
